import {
  format,
  formatDistanceToNow,
  isAfter,
  parseISO,
  subWeeks,
} from "date-fns";
import { formatISOWithOptions, parse } from "date-fns/fp";

const upperCaseFirst = (input: string) =>
  input.charAt(0).toUpperCase() + input.substring(1);

const capitalize = (string: string) =>
  string
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const toSentence = (arr: string[]) =>
  arr.join(", ").replace(/,\s([^,]+)$/, " and $1");

type DateObject = {
  day?: string;
  month?: string;
  year?: string;
};

const serializeDate = (date: DateObject) => {
  if (!date.year || !date.month || !date.day) {
    return "";
  }

  return ["year", "month", "day"]
    .map((key) => date[key as keyof DateObject])
    .filter((val) => !!val)
    .join("-");
};

const deserializeDate: (date: string) => DateObject = (date) => {
  if (!date) return {};

  const parts = date.split("-");
  if (parts.length !== 3) return {};

  return {
    year: parts[0],
    month: parts[1],
    day: parts[2],
  };
};

const parseISODate = parse(new Date(0))("yyyy-MM-dd");

const formatISODate = formatISOWithOptions({ representation: "date" });

type AsCurrencyOptions = {
  currency?: string;
  wholeNumber?: boolean;
};
const asCurrency = (
  number: unknown,
  options: AsCurrencyOptions = { currency: "GBP", wholeNumber: false },
) =>
  Number(number).toLocaleString(undefined, {
    style: "currency",
    currency: options.currency || "GBP",
    ...(options.wholeNumber && {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  });

const formatPence = (pence: number, options?: AsCurrencyOptions) =>
  asCurrency(pence / 100, options);

const stringifyNull = (value: string | number | boolean | null) =>
  value == null ? "" : String(value);

const camelCaseToHyphen = (input: string) => {
  return input.replace(/([A-Z])/g, (g) => `-${g[0]?.toLowerCase() || ""}`);
};

const camelCaseToUnderscore = (input: string) => {
  return input.replace(/([A-Z])/g, (g) => `_${g[0]?.toLowerCase() || ""}`);
};

const shorten = (str: string, maxLen: number, separator = " ", suffix = "") => {
  if (!str) {
    return "";
  }

  if (str.length <= maxLen) {
    return str;
  }

  return `${str.substring(0, str.lastIndexOf(separator, maxLen))}${suffix}`;
};

const possessive = (string: string) => {
  const APOSTROPHE_CHAR = "’";

  if (string === "") {
    return string;
  }

  const lastChar = string.slice(-1);
  const endOfWord =
    lastChar.toLowerCase() === "s" ? APOSTROPHE_CHAR : `${APOSTROPHE_CHAR}s`;
  return `${string}${endOfWord}`;
};

const formatSeconds = (s: number) => {
  const [hour, minute, second] = [s / 3_600, (s / 60) % 60, s % 60];

  const output = hour < 1 ? [minute, second] : [hour, minute, second];

  return output.map((v) => `${Math.floor(v)}`.padStart(2, "0")).join(":");
};

const formatRecentDistanceToNowOrDate = (date: Date | string) => {
  const parsed = typeof date === "string" ? parseISO(date) : date;
  const oneWeekAgo = subWeeks(new Date(), 1);

  if (isAfter(parsed, oneWeekAgo)) {
    return `${formatDistanceToNow(parsed)} ago`;
  }

  return format(parsed, "d MMMM yyyy");
};

export {
  asCurrency,
  camelCaseToHyphen,
  camelCaseToUnderscore,
  capitalize,
  deserializeDate,
  formatISODate,
  formatPence,
  formatRecentDistanceToNowOrDate,
  formatSeconds,
  parseISODate,
  possessive,
  serializeDate,
  shorten,
  stringifyNull,
  toSentence,
  upperCaseFirst,
};
